<template>
  <div id="layout" class="flex-2to1">
    <div id="clipboard">{{this.clipboardText}}</div>

    <ArrowsNav
      :show-left="showPreviousBtn"
      :show-right="showNextBtn"
      @on-click-left="saveAndProceed(true)"
      @on-click-right="saveAndProceed(false)"
    />

    <app-articles-panel
      :key="`articles-panel-${key}`"
      ref="articleWrapper"
      :analysisArticle="analysisArticle"
      :articleHeadline="articleHeadline"
      :articleContent="articleContent"
      :showTranslation="showTranslation"
      :currentParagraphIndex="currentParagraphIndex"
      :noOfSelectedSentences="noOfSelectedSentences"
      :activeTab="activeTab"
      :activeSource="activeSource"
      :activeQuoteIndex="activeQuoteIndex"
      :isClipIrrelevant="isAnalysisIrrelevant"
      @updateSourceMentionQuote="appendSourceMentionQuote"
      @onBackToArticlesClick="onBackToArticlesClick"
      @flagAsIrrelevant="onFlagAsIrrelevantClick"
      @toggleShowTranslation="toggleShowTranslation"
      :relevancySetting="relevancy"
      :loading="loading"
      :relevancyLoading="relevancyLoading"
      @showAICodingHistory="onShowAICodingHistory"
    />

    <app-sidebar
        :key="`sidebar-${key}`"
        ref="sidebar"
        :isTagsMenuPinned="isTagsMenuPinned"
        :isTagsMenuOpen="isTagsMenuOpen"
        :isTagsMenuOverflow="isTagsMenuOverflow"
        :currentTagsType="currentTagsType"
        :contentMentions="contentMentions"
        :articleMentions="articleMentions"
        :activeOrganization="activeOrganization"
        :briefMappings="briefMappings"
        :articleTopLine="articleTopLine"
        :activeTab="activeTab"
        :articleOutlet="articleOutlet"
        :keysPressed="currentKeys"
        :missingCategories.sync="missingCategoriesByType"
        @updateTagsMenuData="updateTagsMenuData"
        @setActiveOrganization="setActiveOrganization"
        @deleteMentionTag="deleteMentionTag"
        @deleteOrganizationMention="deleteOrganizationMention"
        @deleteRelationMentionTag="deleteRelationMentionTag"
        @updateMentionSentiment="updateMentionSentiment"
        @recordAction="recordAction"
        @setActiveTab="setActiveTab"
        @showRelationTagMenu="showRelationTagMenu"
        @cloneOrganizationMentions="cloneOrganizationMentions"
        @onPublishAnalysisClick="onPublishAnalysisClick"
        @updateArticleTopLine="saveArticleTopLine"
        @refreshArticleTopLineMetrics="updateArticleTopLineMetrics"
        @updateTranslations="saveTranslations"
        @saveArticleOutlet="saveArticleOutlet"
        @addMentionTag="addMentionTag"
        @menuType="addMenuType"
        @deleteSourceQuote="deleteSourceQuote"
        @setActiveSource="setActiveSource"
        @setActiveQuoteIndex="setActiveQuoteIndex"
        @searchAuthors="searchAuthors"
    />
    <app-tags-menu
        ref="tagsMenu"
        :isTagsMenuPinned="isTagsMenuPinned"
        :isTagsMenuOpen="isTagsMenuOpen"
        :isTagsMenuOverflow="isTagsMenuOverflow"
        :isTagsBusy="tags.categoryIsBusy || tags.organizationIsBusy || this.organizationIsBusy "
        :currentTagsType="currentTagsType"
        :displayTags="displayTags"
        :categoryKey="categoryKey"
        :searchQuery="searchQuery"
        :menuType="menuType"
        :briefMappings="briefMappings"
        :selectedTagKey="selectedTag && selectedTag.key"
        :activeOrganizationObject="activeOrganizationObject"
        @toggleMenuPin="toggleMenuPin"
        @updateTagsMenuData="updateTagsMenuData"
        @addMentionTag="addMentionTag"
        @removeTagFromMentions="removeTagFromMentions"
        @updateTagInMentions="updateTagInMentions"
        @refreshBriefMappings="loadBriefMappings"
    />

    <app-relation-tags-menu 
      v-if="showRelationMenu"
      v-click-outside="(e) => closeRelationTagMenu(e)"
      :isTagsBusy="tags.relationshipIsBusy"
      :tag="relationTag"
      :relationTags="tags.relationshipData.data"
      :overflowPosition="relationTagsMenuPosition"
      @addRelationMentionTag="addRelationMentionTag"
      @closeRelationTagMenu="closeRelationTagMenu"
    />

    <app-confirmation-modal v-if="showBackConfirmationModal"
                            :headerText="$t('confirmation.header.back_to_articles')"
                            :message="$t('confirmation.message.back_to_articles')"
                            @confirmAction="redirectToArticleList"
                            @cancelAction="toggleBackConfirmationModal" />

    <app-confirmation-modal v-if="showUnpublishConfirmationModal"
                            :headerText="$t('confirmation.header.unpublish')"
                            :message="$t('confirmation.message.unpublish')"
                            @confirmAction="publishClipMentions(false)"
                            @cancelAction="toggleUnpublishConfirmationModal" />

    <app-confirmation-modal v-if="showIrrelevantConfirmationModal"
                            :headerText="$t('confirmation.header.irrelevant')"
                            :message="$t('confirmation.message.irrelevant')"
                            @confirmAction="flagAsIrrelevantConfirm"
                            @cancelAction="toggleIrrelevantConfirmationModal" />

    <app-modal v-if="aiCodingData" @closeModal="closeModal" containerClass="modal-lg">
      <div slot="header">
        <div class="d-flex justify-content-between align-items-center">
          <h3>{{ $t('ai_coding_history.title') }}</h3>
          <span class="fas fa-times cursor-pointer p10" v-on:click="closeModal"></span>
        </div>
      </div>
      <form slot="body" class="overflow-auto max-h-400">
        <div v-for="(coding, i) in aiCodingData" :key="i">
          <div class="mb20">
            <label class="text-gray">{{ $t('ai_coding_history.labels.date') }}</label>
            <p>{{coding.date | formatDate}}</p>
          </div>

          <div v-if="'isRelevant' in coding" class="mb20">
            <label class="text-gray">{{ $t('ai_coding_history.labels.relevant') }}</label>
            <p>{{ coding.isRelevant === null ? $t('ai_coding_history.labels.na') : 
              coding.isRelevant ? $t('ai_coding_history.labels.yes') : $t('ai_coding_history.labels.no')}}
            </p>
          </div>

          <div class="mb20" v-if="coding.model && coding.name">
            <label for="" class="text-gray">{{ $t('ai_coding.table.model') }}</label>
            <p>
              {{coding.name}} - {{coding.model}}
            </p>
          </div>

          <div class="mb20" v-if="coding.data">
            <label class="text-gray">{{ $t('ai_coding_history.labels.content') }} <i class="text-xs text-light-gray">({{ $t('ai_coding_history.labels.organization') }} - {{ $t('ai_coding_history.labels.category') }} - {{ $t('ai_coding_history.labels.tag') }})</i></label>
            <p>
              <span :key="j" v-for="(dt, j) in coding.data">
                <div v-if="dt.organization">
                  {{dt.organization}} - {{dt.category}} -
                  <template v-if="dt.tag">
                    {{dt.tag}}
                  </template>
                  <template v-else>
                    <span class="no-content">{{ $t('ai_coding_history.labels.na') }}</span>
                  </template>
                </div>
                <div v-else-if="dt.favourability">
                  {{dt.favourability}} - 
                  <template v-if="Number.isInteger(dt.positive)">
                    <small class="badge px-2 rounded-pill text-bg-success mx-1">{{ $t('ai_coding_history.labels.positive') }} {{dt.positive}}</small>
                  </template>
                  <template v-else>
                    <small class="badge px-2 rounded-pill text-bg-success mx-1 no-content">{{ $t('ai_coding_history.labels.positive_na') }}</small>
                  </template>

                  <template v-if="Number.isInteger(dt.negative)">
                    <small class="badge px-2 rounded-pill text-bg-danger mx-1">{{ $t('ai_coding_history.labels.negative') }} {{dt.negative}}</small>
                  </template>
                  <template v-else>
                    <small class="badge px-2 rounded-pill text-bg-danger mx-1 no-content">{{ $t('ai_coding_history.labels.negative_na') }}</small>
                  </template>
                </div>
              </span>
            </p>
          </div>

          <div v-if="coding.explanation" class="mb20">
            <label class="text-gray">{{ $t('ai_coding_history.labels.explanation') }}</label>
            <p>{{coding.explanation}}</p>
          </div>

          <div v-if="coding.extra" class="mb20">
            <label class="text-gray">{{ $t('ai_coding_history.labels.misc') }}</label>
            <p>{{coding.extra}}</p>
          </div>

          <hr v-if="aiCodingData.length > i + 1" />
        </div>
      </form>
    </app-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import _ from 'lodash';
import moment from 'moment';
import ArrowsNav from "@/components/arrows-nav/";
import ArticlesPanel from '@/components/coding-ui/articles_panel';
import Sidebar from '@/components/coding-ui/sidebar';
import TagsMenu from '@/components/coding-ui/tags_menu';
import RelationTagsMenu from '@/components/coding-ui/relation_tags_menu';
import ConfirmationModal from '@/components/confirmation-modal';
import Modal from '@/components/modal';
import {
  TAG_TYPE,
  TAG_SELECTABILITY,
  STATEMENT_SCORE_LIMIT,
  TAG_CATEGORY,
  SHORTCUT_COMMANDS,
  CODING_TABS,
  ARTICLE_STATUS,
  MENTIONS_ARTICLE_AREA,
} from "@/config/const";
import editor from '@/helpers/editor';
import array from '@/helpers/array';
import SHORTCUT from '@/config/shortcut-keys'
import { clearCase } from "@/helpers/case";

class MissingCategory {
  name;
  id;
  key;

  constructor (data) {
    this.name = data.name;
    this.id = data.id;
    this.key = data.key;
  }
}

class MissingOrganization {
  name;
  orgId;
  isArticle;
  missingCategories = [];
  missingCategories;

  constructor (data, category = null) {
    this.name = data.name;
    this.orgId = data.org_id || null;
    this.createdAt = new Date(data.created_at);
    this.isArticle = data.hasOwnProperty('organizations');

    if (category)
      this.missingCategories = [new MissingCategory(category)];
  }
}

export default {
  components: {
    'app-articles-panel': ArticlesPanel,
    'app-sidebar': Sidebar,
    'app-tags-menu': TagsMenu,
    'app-relation-tags-menu': RelationTagsMenu,
    'app-confirmation-modal': ConfirmationModal,
    'app-modal': Modal,
    ArrowsNav
  },
  computed: {
    ...mapState({
      loading: state => state.analysisArticle.analysisArticle.isBusy,
      relevancy: state => state.relevancy.relevancy?.data?.data || {},
      relevancyLoading: state => state.relevancy.relevancy?.isBusy,
      projectSetupGeneral: state => state?.projectSetup?.projectSetup?.data?.general,
      projectHierarchy: state => state.projectSetup.projectHierarchy?.data,
    }),
    ...mapState('tags', [
      'tags'
    ]),
    ...mapState('briefMappings', [
      'briefMappings'
    ]),
    ...mapState('analysisArticle', [
      'analysisArticle'
    ]),
    ...mapState('analysisArticlePublish', [
      'analysisArticlePublish'
    ]),
    ...mapState('articleTopLine', [
      'articleTopLine'
    ]),
    ...mapState('authors', [
      'authors'
    ]),
    ...mapState('editorProjectSettings', [
      'editorProjectSettings'
    ]),
    ...mapState('analysisArticleIrrelevant', [
      'analysisArticleIrrelevant'
    ]),
    showPreviousBtn() {
      return !!_.get(this.analysisArticle, 'data.pagination.previousArticle.id');
    },
    showNextBtn() {
      return !!_.get(this.analysisArticle, 'data.pagination.nextArticle.id');
    },
    requiredCategories() {
      if (!this.allTagsCategories.length) return [];
      
      return this.allTagsCategories.filter(category => {
        return category.required && !this.isArticleLevelCategory(category.category);
      });
    },
    requiredArticleLevelCategories() {
      if (!this.allTagsCategories.length) return [];
      
      return this.allTagsCategories.filter(category => {
        return category.required && this.isArticleLevelCategory(category.category);
      });
    },
    missingCategories() {
      const missingCategories = [];

      this.requiredArticleLevelCategories.forEach(requiredCategory => {
        const hasArticleMentions = this.articleMentions.find(articleMention => {
          return requiredCategory.key === articleMention.parent;
        });

        if (!hasArticleMentions) missingCategories.push(requiredCategory);
      })

      this.requiredCategories.forEach((requiredCategory) => {
        let hasInAllCategories = false;

        if (this.contentMentions && this.contentMentions.length) {
          const hasContentMention = this.contentMentions.filter((mention) => {
            return (
              mention.tags &&
              mention.tags.find((tag) => {
                return requiredCategory.key === tag.parent;
              })
            );
          });

          if (hasContentMention.length >= this.contentMentions.length)
            hasInAllCategories = true;
        }

        if (!hasInAllCategories) {
          requiredCategory.organizations = this.contentMentions.filter((mention) => {
            return !(
              mention.tags &&
              mention.tags.find((tag) => {
                return requiredCategory.key === tag.parent;
              })
            );
          });

          missingCategories.push(requiredCategory);

        }
      });

      return missingCategories;
    },
    missingCategoriesByType() {
      const contentList = [];
      const articleList = []

      this.missingCategories.forEach(category => {
        if (category.hasOwnProperty('organizations')) {

          category.organizations.forEach(organization => {
            const alreadyHasOrg = contentList.find(item => {
              return item.orgId === organization.org_id;
            });

            if (!alreadyHasOrg) {
              const newOrg = new MissingOrganization(organization, category);
              contentList.push(newOrg);
            } else {
              alreadyHasOrg.missingCategories.push(new MissingCategory(category));
            }
          })

        } else {
          articleList.push(new MissingCategory(category));
        }
      });

      const data = {
        article: articleList,
        content: _.sortBy(contentList, ['createdAt']),
      }

      return data;
    },
    projectId() {
      return this.$route.params.project_id;
    },
    articleId() {
      return this.$route.params.article_id;
    },
    showScoringSystem () {
      return this.editorProjectSettings.data && this.editorProjectSettings.data.scoring_system && this.editorProjectSettings.data.scoring_system.toLowerCase() !== "none";
    },
    articleOutlet () {
      return this.analysisArticle && this.analysisArticle.data && this.analysisArticle.data.data.outlet;
    },
    isAnalysisIrrelevant () {
      return this.analysisArticleIrrelevant && this.analysisArticleIrrelevant.data !== null?
        this.analysisArticleIrrelevant.data : this.analysisArticle && this.analysisArticle.data ?
          this.analysisArticle.data.data.analysis_state == ARTICLE_STATUS.IRRELEVANT :
          null;
    },
    // For MMI projects
    isUseOrgWithCategory() { return this.projectSetupGeneral?.organizations_with_category },
    organizationCategoryId() { return this.projectSetupGeneral?.organizations_category_id },
    activeOrganizationObject () {
      return _.cloneDeep(this.contentMentions.find(mention => editor.stringToClass(mention.name) === this.activeOrganization) && this.isUseOrgWithCategory && this.organizationCategoryId);
    },
  },
  watch: {
    "tags.categoryData": {
      handler (newValue) {
        if(!this.categoryKey) {
          this.categoryList = _.cloneDeep(newValue);
        }

        if(this.currentTagsType === TAG_TYPE.CATEGORY && newValue.data !== this.displayTags) {
          // When current tags being displayed are of type category and the store data for tags.categoryData changes
          // change the displayTags object with new tags
          this.displayTags = newValue.data;
        }
      },
      deep: true
    },
    "tags.organizationData": {
      handler (newValue) {
        if(this.currentTagsType === TAG_TYPE.ORGANIZATION && newValue.data !== this.displayTags) {
          // When current tags being displayed are of type organization and the store data for tags.organizationData
          // changes, change also the displayTags object with new tags
          this.displayTags = newValue.data;
        }
      },
      deep: true
    },
    "analysisArticle.data.data": {
      handler (analysisArticleData) {

        if(this.currentClip && analysisArticleData.id && analysisArticleData.id != this.currentClip) {
          this.redirectToClip(this.currentClip);
        }

        if (this.articleContent.length === 0 && this.articleHeadline === '') {
          this.setArticleContent(analysisArticleData, true);
        }

        let mentions = analysisArticleData.mentions;

        if (this.activeOrganization === '') {
          const defaultActiveOrganization = this.getDefaultActiveOrganization(mentions);
          this.setActiveOrganization(defaultActiveOrganization);
        }

        if (Object.keys(this.mentions).length === 0 && !_.isEqual(this.mentions, mentions)) {
          this.updateMentions(this.initMentions());
          this.setArticleContent(analysisArticleData, true);
        }
      },
      deep: true
    },
    "articleTopLine.data": {
      handler () {
        if(this.isToplineUpdated) {
          this.initArticle(true, this.resetContent);
          this.isToplineUpdated = false;
          this.resetContent = false;
        }
      },
      deep: true
    },
    $route: {
      handler () {
        this.initArticle();

        this.setActiveOrganization('');
        this.setActiveTab(CODING_TABS.EDITOR);
      }
    },
    activeTab: {
      handler(newValue, oldValue) {
        if(oldValue === CODING_TABS.SOURCES && newValue !== CODING_TABS.SOURCES) {
          //reset source values
          this.setActiveSource();
          this.setActiveQuoteIndex();
        }
      }
    },
    categoryKey: {
      handler (value) {
        this.loadBriefMappings();
      }
    }
  },
  mounted () {
    this.currentClip = this.articleId;
    this.initArticle();

    this.loadBriefMappings();

    if(!this.tags.organizationIsBusy) {
      this.setTagsData(TAG_TYPE.ORGANIZATION);
    }

    this.getprojectSetup({ projectUid: this.projectId }).then((data)=>{
      if(!this.tags.categoryIsBusy) {
        this.setTagsData(TAG_TYPE.CATEGORY, this.categoryKey);
      }
      if(!data?.general?.organizations_with_category) return;
      // get all categories
      this.getProjectCategoriesSetup({ projectUid: this.projectId })
    });

    if(!this.tags.relationshipIsBusy) {
      this.getRelationshipTags({
        projectId: this.projectId,
        searchQuery: this.relationSearch
      });
    }
    
    if (!this.authors.isBusy) {
      this.getAuthors({
        projectId: this.projectId
      });
    }

    if(!this.editorProjectSettings.isBusy) {
      this.getProjectSettings({
        projectId: this.projectId
      });
    }
  },
  created() {
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    this.getRelevancy({projectId: this.projectId, type: 'Coding'});
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
  },
  methods: {
    ...mapActions({
      'getBriefMappings': 'briefMappings/getBriefMappings',
      'getOrganizationTags': 'tags/getOrganizationTags',
      'getCategoryTags': 'tags/getCategoryTags',
      'getRelationshipTags': 'tags/getRelationshipTags',
      'getArticle': 'analysisArticle/getAnalysisArticle',
      'publishAnalysisArticle': 'analysisArticlePublish/publishAnalysisArticle',
      'updateAnalysisArticle': 'analysisArticle/updateAnalysisArticle',
      'refreshAnalysisArticle': 'analysisArticle/refreshAnalysisArticle',
      'getArticleTopLine': 'articleTopLine/getArticleTopLine',
      'updateArticleTopLine': 'articleTopLine/updateArticleTopLine',
      'refreshArticleTopLineMetrics': 'articleTopLine/refreshArticleTopLineMetrics',
      'updateTranslations': 'articleTopLine/updateTranslations',
      'getAuthors': 'authors/getAuthors',
      'getProjectSettings': 'editorProjectSettings/getProjectSettings',
      'flagAsIrrelevant': 'analysisArticleIrrelevant/flagAsIrrelevant',
      'setArticleIsPublished': 'analysisArticlePublish/setArticleIsPublished',

      'addRelationshipLine': 'analysisArticle/addRelationshipLine',
      'cloneRelationshipLine': 'analysisArticle/cloneRelationshipLine',
      'updateRelationshipLineScores': 'analysisArticle/updateRelationshipLineScores',
      'addTag': 'analysisArticle/addTag',
      'updateTag': 'analysisArticle/updateTag',
      'deleteMention': 'analysisArticle/deleteMention',
      'updateSourceMention': 'analysisArticle/updateSourceMention',
      'deleteSourceMention': 'analysisArticle/deleteSourceMention',
      'undoRelationshipLineAction': 'analysisArticle/undoAction',
      'getRelevancy' : "relevancy/getRelevancy",

      "getprojectSetup": "projectSetup/getprojectSetup",
      "getProjectCategoriesSetup": "projectSetup/getProjectCategoriesSetup",
    }),
    onShowAICodingHistory (data) {
      this.aiCodingData = data;
    },
    searchAuthors (text) {
      this.getAuthors({ projectId: this.projectId, search: text });
    },
    isArticleLevelCategory(categoryType) {
      return Object.entries(MENTIONS_ARTICLE_AREA)
        .find(category => category[1] === categoryType);
    },
    initArticle(articleOnly, resetContent = true) {
      if (!this.analysisArticle.isBusy) {
        if (resetContent) {
          this.articleContent = [];
          this.articleHeadline = '';
          this.mentions = [];
        }

        this.getArticle({
          projectId: this.projectId,
          articleId: this.articleId
        })
      }

      if (!this.articleTopLine.isBusy && !articleOnly) {
        this.getArticleTopLine({
          projectId: this.projectId,
          articleId: this.articleId
        });
      }
    },

    initMentions () {
      return _.cloneDeep(_.get(this.analysisArticle, 'data.data.mentions'));
    },

    loadBriefMappings (forceGet, categoryKey) {
      return new Promise((resolve, reject) => {
        if(!this.briefMappings.isBusy) {
          return this.getBriefMappings({
            projectId: this.projectId,
            forceGet: forceGet,
            categoryKey: categoryKey || this.categoryKey
          }).then(r => resolve(r));
        }
        resolve();
      });
    },

    onBackToArticlesClick (e) {
      if(this.analysisArticle && this.analysisArticle.data && !_.isEqual(this.analysisArticle.data.data.mentions, this.mentions)) {
        this.toggleBackConfirmationModal(true, e)
      }
      else {
        this.redirectToArticleList();
      }
    },
    toggleBackConfirmationModal (open, e) {
      if(e) {
        e.preventDefault();
        e.stopPropagation();
      }

      this.forceQuit = open;
      this.showBackConfirmationModal = open;
    },
    redirectToArticleList (e) {
      if(e) {
        e.preventDefault();
        e.stopPropagation();
      }

      const path = editor.getPathFromSearch(this.projectId, window.location.search);
      this.$router.push(path);
    },
    getTag (tagKey) {
      return this.briefMappings?.data?.data[tagKey];
    },
    getTagOrganization(tag) {
      return tag.category === TAG_CATEGORY.ARTICLE_LEVEL ||
      tag.category === TAG_CATEGORY.ARTICLELEVEL ||
        tag.category === TAG_CATEGORY.MEDIA_TAG
        ? "articleLevel"
        : tag.category === TAG_CATEGORY.ORGANIZATION
        ? editor.stringToClass(tag.name)
        : this.activeOrganization;
    },
    async setTagsData (tagType, searchQuery, categoryKey, shortcutSearch, parentTagId) {
      if(_.isUndefined(searchQuery))
        searchQuery = this.searchQuery;
      if(_.isUndefined(tagType))
        tagType = this.currentTagsType;
      let filters ={}
      if(this.projectSetupGeneral?.organizations_with_category){
        filters.organizations_category_id = this.projectSetupGeneral?.organizations_category_id
      }

      switch(tagType) {
        case TAG_TYPE.CATEGORY:
          if(this.activeOrganizationObject?.id){
            filters.active_organization_id = this.activeOrganizationObject?.id;
          }
          return this.getCategoryTags({
            projectId: this.projectId,
            categoryKey: categoryKey,
            searchQuery: searchQuery,
            shortcutSearch: shortcutSearch,
            pagination: { page: 1, perPage: 100, order: "id", sort: "asc" },
            filters: filters
          })
            .then(data => {
              if (!categoryKey) {
                this.setAllTagsCategories(data);
                return data;
              }
              else {
                return this.loadBriefMappings(false, categoryKey).then(r => data);
              }
            });
        case TAG_TYPE.ORGANIZATION:
          this.organizationIsBusy = true;
          return this.getOrganizationTags({
            projectId: this.projectId,
            searchQuery: searchQuery,
            parentTagId: parentTagId
          }).then(data => {
            return new Promise(resolve => {
              if (data?.data && Object.keys(data.data).length > 0) {
                const firstEntry = Object.values(data.data)[0];
                const key = firstEntry.parent;
                if (key) {
                  this.loadBriefMappings(false, key).then(() => resolve());
                } else {
                  resolve();
                }
              }
              resolve();
            });
          }).finally(() => {
            this.organizationIsBusy = false;
          });
        default:
          return Promise.resolve();
      }
    },
    setAllTagsCategories (data) {
      this.allTagsCategories =  Object.keys(data).map(item => {
        return data[item];
      });
    },
    updateTagsMenuData (isTagsMenuOpen, currentTagsType, overflow, searchQuery, tagKey, shortcutSearch, forceGet = false, tagObj, isPinning) {
      this.isTagsMenuOverflow = overflow && overflow.enabled && !this.isTagsMenuPinned? overflow: null;
      // use local var to avoid duplicate calls, when click on organization categroy
      let _tagKey = tagKey; 
      if(isTagsMenuOpen) {
        //Get data of tags to show
        let tagsData = {}, filteredOrgs = {data: {}};

        if(currentTagsType === TAG_TYPE.ORGANIZATION) {
          // filter organizations based on parent tag only in organization with category mode
          if(this.isUseOrgWithCategory && tagObj) {
            // call/load organization list if there is no organization has been set in the article details/coding sections
            let _mentions = _.cloneDeep(this.mentions);
            delete _mentions[TAG_CATEGORY.ARTICLELEVEL];
            delete _mentions[TAG_CATEGORY.ARTICLE_LEVEL];
            delete _mentions[TAG_CATEGORY.MEDIA_TAG];
            let tagType = !Object.keys(_mentions)?.length && this.projectSetupGeneral.org_category_key !== tagObj?.parent ? TAG_TYPE.CATEGORY : currentTagsType;
            let tagCategoryId = !Object.keys(_mentions)?.length && this.projectSetupGeneral.org_category_key !== tagObj?.parent ? this.projectSetupGeneral.org_category_key : tagObj.parent;
            this.parentTagId = tagObj.id
            this.setTagsData(tagType, searchQuery, tagCategoryId, shortcutSearch, this.parentTagId).then(data => {
              if(data) this.displayTags = data;
            });
          }
          else{
            tagsData = this.tags.organizationData;
          }
        }
        else if(currentTagsType === TAG_TYPE.CATEGORY) {
          if(isPinning) {
            tagsData = this.tags.categoryData;
          }
          else {
            let categories = tagKey || _.isEmpty(this.categoryList) ? this.tags.categoryData : this.categoryList;
            // if parent key of the loaded tags keys are same as the main org category key, call categories list
            if(this.isUseOrgWithCategory && Object.values(categories?.data)?.[0]?.parent === this.projectSetupGeneral.org_category_key && !tagKey) {
              this.setTagsData(currentTagsType, searchQuery, undefined, shortcutSearch, this.parentTagId);
            }
            else if(this.isUseOrgWithCategory && !tagObj && this.menuType === TAG_TYPE.ORGANIZATION){
              tagsData = Object?.entries(categories?.data)?.reduce((acc, [k, v]) => v?.id === this.organizationCategoryId && (acc['data'][k] = v) && acc || acc, {data: {}});
              if(!tagKey) tagKey = Number(Object.keys(tagsData?.data)?.[0]?.[0]) ? +Object.keys(tagsData?.data)?.[0]?.[0] : tagKey;
            }
            else{
              tagsData = categories;
            }
          }
        }
        else {
          tagsData = undefined;
        }

        // if forceGet or data undefined or searching query changed, get data again
        // otherwise, set display tags
        if(forceGet || _.isUndefined(tagsData) || this.searchQuery !== searchQuery ||
          (currentTagsType === TAG_TYPE.CATEGORY && tagKey && this.categoryKey !== tagKey)) {
          this.setTagsData(currentTagsType, searchQuery, tagKey, shortcutSearch, this.parentTagId);
        }
        else if(tagsData?.data && !_.isEqual(this.displayTags, tagsData.data)) {
          this.displayTags = tagsData.data;
        }

        //When switching tag menu and there was a change in the reset the other tags data
        if(currentTagsType !== this.currentTagsType && (this.searchQuery !== searchQuery ||
          (this.currentTagsType === TAG_TYPE.CATEGORY && tagKey && this.categoryKey !== tagKey)))
        {
          this.setTagsData(this.currentTagsType, searchQuery, tagKey, shortcutSearch);
        }

        this.searchQuery = searchQuery;
        this.shortcutSearch = shortcutSearch
        this.categoryKey = _tagKey || tagKey;
        this.selectedTag = undefined;
      }

      this.repositionSlideMenu(isTagsMenuOpen, overflow);
      this.toggleMenuOpen(isTagsMenuOpen, currentTagsType);
    },
    repositionSlideMenu (isTagsMenuOpen, overflow) {
      let tagsMenu = document.getElementById('tags-menu');
      //reset to default
      tagsMenu.removeAttribute('style');
      if(this.isTagsMenuOverflow) {
        tagsMenu.style.transition = "none";
        tagsMenu.style.height = "60vh";
      } else {
        tagsMenu.style.transition = "margin 0.2s";
        tagsMenu.style.height = "100vh";
      }

      if(!isTagsMenuOpen) {
        return;
      }

      if(this.isTagsMenuOverflow && !this.isTagsMenuPinned) {
        const clickX = overflow.xCoordinate, clickY = overflow.yCoordinate;
        const w = window.innerWidth, h = window.innerHeight;
        const menuWidth = tagsMenu.offsetWidth, menuHeight = tagsMenu.offsetHeight;

        if (clickX + menuWidth > w)
          tagsMenu.style.right = "0";
        else
          tagsMenu.style.left = overflow.xCoordinate + "px";

        if (clickY + menuHeight > h)
          tagsMenu.style.bottom = "5px";
        else
          tagsMenu.style.top = overflow.yCoordinate + "px";

        tagsMenu.querySelector("#tags-menu-header").style.display = "none";
      }
      else {
        tagsMenu.querySelector("#tags-menu-header").style.display = "flex";
      }
    },
    toggleMenuPin (isTagsMenuPinned) {
      this.isTagsMenuPinned = isTagsMenuPinned;
    },
    toggleMenuOpen (isTagsMenuOpen, currentTagsType) {
      this.isTagsMenuOpen = isTagsMenuOpen;
      this.currentTagsType = currentTagsType;
      if(!isTagsMenuOpen) this.parentTagId = null;
    },
    setActiveOrganization (organizationName) {
      if (this.activeOrganization !== organizationName) {
        this.activeOrganization = organizationName;
      }
    },
    addMenuType(menuType = ''){
      this.menuType = menuType;
    },
    addMentionTag (tag, isAssociated = false, actionAfter) {
      // If a tag that requires an organization mention is added, and none were yet added, save tag in
      // 'pendingMentionTag' to add once the coder adds an organization.
      if (editor.stringToClass(tag.category) !== TAG_CATEGORY.ORGANIZATION &&
        (this.activeOrganization === '' && tag.category !== TAG_CATEGORY.ARTICLE_LEVEL &&
        tag.category !== TAG_CATEGORY.ARTICLELEVEL) || (this.isUseOrgWithCategory && !tag?.tags && this.currentTagsType === TAG_TYPE.CATEGORY && this.menuType === TAG_TYPE.ORGANIZATION && !isAssociated))
      {
        this.pendingMentionTag = tag;
        this.searchQuery = '';
        this.updateTagsMenuData(true, TAG_TYPE.ORGANIZATION, this.isTagsMenuOverflow, this.searchQuery, tag.key, undefined, undefined, this.isUseOrgWithCategory && tag);
        return;
      }

      //If tag is not in brief mappings force refresh brief mappings
      const briefMappingTag = this.getTag(tag.key);
      if (!briefMappingTag) {
        this.loadBriefMappings(true);
      }

      const tagName = editor.stringToClass(tag.name);

      let articleLevelMentions;
      if(this.mentions[TAG_CATEGORY.ARTICLELEVEL]) articleLevelMentions = _.clone(this.mentions[TAG_CATEGORY.ARTICLELEVEL]);

      //If tag has category 'organisation' check if already added to mentions and set to activeOrganization
      if (tag.category.toLowerCase() === TAG_CATEGORY.ORGANIZATION) {
        if (!this.mentions[tagName]) {
          this.addRelationshipLine({
            projectId: this.projectId,
            articleId: this.articleId,
            organizationId: tag.id
          }).then((response) => {
            this.recordAction(response[tagName], true, articleLevelMentions);
            this.updateMentions(response);

            if(!_.isEmpty(this.pendingMentionTag)) {
              const pendingTag = _.cloneDeep(this.pendingMentionTag);
              // In case a user attempts to add one organization after the other,
              // we need to use the previously selected "category tag"
              if (!this.isUseOrgWithCategory) {
                this.pendingMentionTag = {};
              }
              // this.pendingMentionTag = {};
              this.addMentionTag(pendingTag, true);
            }
            else if (typeof actionAfter === 'function') {
              if (this.selectedTag) this.selectedTag.relationship_line_id = response[tagName].relationship_line_id;
              actionAfter();
            }
          });
        }

        //set active organisation
        this.setActiveOrganization(tagName);
      }
      else {
        let organization = this.getTagOrganization(tag);

        //If not an associated tag, and the relationshipline already exists log mention before adding tag
        if(!isAssociated && this.mentions[organization]) {
          this.recordAction(tag);
        }

        let tags = this.mentions[organization] ? this.mentions[organization].tags : [];

        const isEditSource = tag.category.toLowerCase() === TAG_CATEGORY.SOURCES && tags.find(mentionTag => mentionTag.id && mentionTag.id === tag.id);

        if ((organization === TAG_CATEGORY.ARTICLELEVEL || organization === TAG_CATEGORY.ARTICLE_LEVEL) && !this.mentions[organization]) {
          this.addRelationshipLine({
            projectId: this.projectId,
            articleId: this.articleId,
            organizationId: null,
            tag: tag
          }).then((response) => {
            this.recordAction(response[organization], true);
            this.updateMentions(response);

            if (typeof actionAfter === 'function') {
              if (this.selectedTag) this.selectedTag.relationship_line_id = response[organization].relationship_line_id;
              actionAfter();
            }
          });
        }
        else if(isEditSource) {
          this.updateSourceMention({
            projectId: this.projectId,
            articleId: this.articleId,
            relationshipLineId: this.mentions[organization] ? this.mentions[organization].relationship_line_id : null,
            organizationKey: organization,
            tagId: tag.id,
            quote: tag.mentions[this.activeQuoteIndex]
          }).then((response) => {
            this.updateMentions(response);
          });

          //check if it was already added
        } else if(tag.category.toLowerCase() === TAG_CATEGORY.SOURCES || !tags.find(t => t.key === tag.key)){
          let tagToRemove;
          if(tag.selectability === TAG_SELECTABILITY.ONLYONE) {
            tagToRemove = _.cloneDeep(tags.find(t => t.parent === tag.parent));
          }

          this.addTag({
            projectId: this.projectId,
            articleId: this.articleId,
            relationshipLineId: this.mentions[organization] ? this.mentions[organization].relationship_line_id : null,
            tag
          }).then((response) => {
            this.updateMentions(response);

            //check if tag is of type oneOnly... if so remove highlights of previous tag
            if(tag.selectability === TAG_SELECTABILITY.ONLYONE && tagToRemove) {
              this.removeHighlight(tagToRemove);
            }

            if (typeof actionAfter === 'function') {
              if (this.selectedTag) this.selectedTag.relationship_line_id = response[organization].relationship_line_id;
              actionAfter();
            }
          });
        }
      }
      //Highlight any tag added
      this.highlight(tag, false);

      if (tag.associates && tag.associates.length > 0) {
        tag.associates.forEach(key =>{
          const associatedTag = this.getTag(key);
          this.highlight(associatedTag, false);
        })
      }
    },
    addRelationMentionTag(tag, relationTag) {
      const organization =
        tag.category === TAG_CATEGORY.ARTICLE_LEVEL ||
        tag.category === TAG_CATEGORY.ARTICLELEVEL ||
        tag.category === TAG_CATEGORY.MEDIA_TAG
          ?
          'articleLevel' :
          this.activeOrganization;

      this.recordAction(tag);

      //check if it was already added
      if(this.mentions[organization].tags.find(t => t.key === tag.key)) {
        if(this.showScoringSystem && relationTag.show_sentiment) {
          relationTag.positive_score = 0;
          relationTag.negative_score = 0;
        }
        else {
          relationTag.positive_score = null;
          relationTag.negative_score = null;
        }

        this.updateTag({
          projectId: this.projectId,
          articleId: this.articleId,
          relationshipLineId: tag.relationship_line_id,
          organizationKey: organization,
          tag: tag,
          relationTag
        }).then((response) => {
          this.updateMentions(response);
        });
      }
    },
    deleteMentionTag ({ tag, organization }) {
      if (!organization || !Object.keys(organization).length) {
        organization =
          tag.category === TAG_CATEGORY.ARTICLE_LEVEL ||
          tag.category === TAG_CATEGORY.ARTICLELEVEL ||
          tag.category === TAG_CATEGORY.MEDIA_TAG
            ? "articleLevel"
            : this.activeOrganization;
      }

      if (typeof organization === 'object' && organization.name) 
        organization = organization.name;

      if (organization.toLowerCase() !==  TAG_CATEGORY.ARTICLELEVEL.toLowerCase() && organization.toLowerCase() !==  TAG_CATEGORY.ARTICLE_LEVEL.toLowerCase()) {
        organization = clearCase(organization);
      }

      const tagToRemove = _.cloneDeep(this.mentions[organization].tags.find(t => t.id === tag.id));

      this.deleteMention({
        projectId: this.projectId,
        articleId: this.articleId,
        relationshipLineId: this.mentions[organization].relationship_line_id,
        organizationKey: organization,
        tag
      }).then((response) => {
        this.updateMentions(response);
        this.removeHighlight(tagToRemove);
      });
    },
    deleteSourceQuote({sourceId, quoteIndex}) {
      const sourceMentionTags = _.cloneDeep(this.mentions[this.activeOrganization].tags.find(t => t.id === sourceId));

      //log mentions before removing quote
      this.recordAction(sourceMentionTags);

      if(quoteIndex === 0 && sourceMentionTags.mentions.length === 1) {
        this.deleteMentionTag({ tag: sourceMentionTags, organization: this.activeOrganization });
      }
      else {
        this.deleteSourceMention({
          projectId: this.projectId,
          articleId: this.articleId,
          relationshipLineId: this.mentions[this.activeOrganization] ? this.mentions[this.activeOrganization].relationship_line_id : null,
          organizationKey: this.activeOrganization,
          tagId: sourceMentionTags.id,
          quoteId: sourceMentionTags.mentions[quoteIndex].id
        }).then((response) => {
          this.updateMentions(response);
        });
      }
    },
    deleteOrganizationMention (organizationName) {
      if(this.isTagsMenuOpen) {
        this.updateTagsMenuData(false);
      }

      const tagToRemove = _.cloneDeep(this.mentions[organizationName]);

      this.deleteMention({
        projectId: this.projectId,
        articleId: this.articleId,
        relationshipLineId: tagToRemove.relationship_line_id,
        organizationKey: organizationName
      }).then((response) => {
        this.updateMentions(response);
        this.removeHighlight(tagToRemove);

        if(this.activeOrganization === organizationName) {
          this.setActiveOrganization(this.getDefaultActiveOrganization());
        }
      });
    },
    deleteRelationMentionTag (tag) {
      if(this.isTagsMenuOpen) {
        this.updateTagsMenuData(false);
      }

      const organization = 
        tag.category === TAG_CATEGORY.ARTICLE_LEVEL ||
        tag.category === TAG_CATEGORY.ARTICLELEVEL ||
        tag.category === TAG_CATEGORY.MEDIA_TAG
        ?
         'articleLevel' :
        this.activeOrganization;

      this.updateTag({
        projectId: this.projectId,
        articleId: this.articleId,
        relationshipLineId: tag.relationship_line_id,
        organizationKey: organization,
        tag: tag,
        deleteRelationTag: true
      }).then((response) => {
        this.updateMentions(response);
      });
    },
    updateMentionSentiment ({tag, isPositive, isRelation = false, isReduce = false, scoreUpdated}) {
      if(this.showScoringSystem === false || this.showScoringSystem === undefined)
        return;

      //get organization mentions
      const mentionsOrganization = this.getTagOrganization(tag);

      //clone tag and update score
      let newTag = _.clone(tag);

      let scoreTag = isRelation ? newTag.relation_tag : newTag;

      let positiveUpdate = 0; let negativeUpdate = 0;
      if(!scoreUpdated) {
        positiveUpdate = isPositive ? 1 : 0;
        negativeUpdate = !isPositive ? 1 : 0;
      }
      else {
        positiveUpdate = scoreUpdated.positiveScore;
        negativeUpdate = scoreUpdated.negativeScore;
      }

      //update score
      scoreTag.positive_score = isReduce ? (scoreTag.positive_score || 0) - positiveUpdate : (scoreTag.positive_score || 0) + positiveUpdate;
      scoreTag.negative_score = isReduce ? (scoreTag.negative_score || 0) - negativeUpdate : (scoreTag.negative_score || 0) + negativeUpdate;
      newTag = isRelation ? {...newTag, ...{ relation_tag: scoreTag }} : scoreTag;

      //set the score limit
      const newScore = editor.getTagScore(newTag, isPositive);
      if(newScore < 0 || (STATEMENT_SCORE_LIMIT !== -1 && newScore > STATEMENT_SCORE_LIMIT))
        return;

      if(tag.category === TAG_CATEGORY.ORGANIZATION) {
        this.updateRelationshipLineScores({
          projectId: this.projectId,
          articleId: this.articleId,
          relationshipLineId: tag.relationship_line_id,
          organizationKey: mentionsOrganization,
          tagScores: { positive_score: newTag.positive_score, negative_score: newTag.negative_score }
        }).then((response) => {
          this.updateMentions(response);
        });
      }
      else {
        this.updateTag({
          projectId: this.projectId,
          articleId: this.articleId,
          relationshipLineId: tag.relationship_line_id,
          organizationKey: mentionsOrganization,
          tag: tag,
          tagScores: {
            positive_score: newTag.positive_score,
            negative_score: newTag.negative_score
          },
          relationTag: newTag.relation_tag
        }).then((response) => {
          this.updateMentions(response);
        });
      }

      return newTag;
    },

    updateMentions (mentions) {
      if(!mentions) {
        mentions = this.mentions;
      }
      else {
        this.mentions = mentions;
      }

      this.contentMentions = mentions ? _.filter(mentions, (m, key) => key !== 'articleLevel') : [];
      this.articleMentions = mentions && mentions.articleLevel ? _.clone(mentions.articleLevel.tags) : [];
    },
    getDefaultActiveOrganization (mentions) {
      if(_.isUndefined(mentions))
        mentions = this.mentions;

      let activeOrganization = '';

      if(mentions && Object.keys(mentions).length > 0) {
        activeOrganization = Object.keys(mentions)[0];

        if(activeOrganization === 'articleLevel') {
          if (Object.keys(mentions).length > 1) {
            activeOrganization = Object.keys(mentions)[1];
          }
          else {
            activeOrganization = '';
          }
        }
      }

      return activeOrganization;
    },
    recordAction (tag, isAddRelationshipLine, articleLevelMentions) {
      if(this.coderActions.length >= 10) {
        this.coderActions.shift();
      }

      const organization = this.getTagOrganization(tag);
      let mentions = {};

      if (isAddRelationshipLine) {
        mentions[organization] = null;
        if(organization !== TAG_CATEGORY.ARTICLELEVEL) {
          mentions[TAG_CATEGORY.ARTICLELEVEL] = articleLevelMentions || null;
        }
      }
      else {
        mentions[organization] = _.cloneDeep(this.mentions[organization]);
        if(organization !== TAG_CATEGORY.ARTICLELEVEL) {
          mentions[TAG_CATEGORY.ARTICLELEVEL] = this.mentions[TAG_CATEGORY.ARTICLELEVEL] || null;
        }
      }

      this.coderActions.push({
        organization,
        relationshipLineId: tag.relationship_line_id || this.mentions[organization].relationship_line_id,
        mentions
      });
    },
    handleKeyDown (e) {
      var node = (e.target) ? e.target : ((e.srcElement) ? e.srcElement : null);
      if(node.nodeName === 'INPUT' || node.closest('.form-control'))
        return;

      this.currentKeys.push({ key: e.key.toLowerCase(), keyCode: e.keyCode || e.which });
    },
    handleKeyUp (e) {
      var node = (e.target) ? e.target : ((e.srcElement) ? e.srcElement : null);
      if(node.nodeName === 'INPUT' || node.closest('.form-control'))
        return;

      const pressedKey = e.keyCode || e.which;
      let command;
      const commandKeyCodes = [16, 17, 18, 91, 93];

      //if key is a command key and it was the only one recorded on keydown, get relevant command
      if(commandKeyCodes.indexOf(pressedKey) !== -1) {
        if(this.currentKeys.length === 1) {
          command = Object.keys(SHORTCUT.shortcuts).filter(shortcut => {
            const item = SHORTCUT.shortcuts[shortcut];

            if (item.length !== 1) return;
            
            return (
              (item[0].key === e.key.toLowerCase() && item[0].keyCode === pressedKey) ||
              (
                item[0].alternative &&
                item[0].alternative.key === e.key.toLowerCase() &&
                item[0].alternative.keyCode === pressedKey
              )
            );
          });

          //clear currentKeys
          this.clearCurrentKeys();
        }
      }
      //if not a command key
      else {
        //check if any on the command key pressed is true
        const shortcutKeysPressed = [{ key: e.key.toLowerCase(), keyCode: pressedKey }];
        if(e.ctrlKey)
          shortcutKeysPressed.push({ key: 'control', keyCode: 17 });
        if(e.shiftKey)
          shortcutKeysPressed.push({ key: 'shift', keyCode: 16 });
        if(e.altKey)
          shortcutKeysPressed.push({ key: 'alt', keyCode: 18 });
        if(e.metaKey)
          shortcutKeysPressed.push({ key: 'meta'});

        const sortedCurrentKeys = array.sortArrayByString(shortcutKeysPressed, 'key');
        //find command with those keys and execute

        command = Object.keys(SHORTCUT.shortcuts).filter(shortcut => {
          const sortedShortcut = array.sortArrayByString(SHORTCUT.shortcuts[shortcut], 'key');

          const simpleShortcut = sortedShortcut.map(item => {
            return { key: item.key, keyCode: item.keyCode }
          });

          const simpleShortcutAlternative = sortedShortcut.map(item => {
            return {
              key: item.alternativeKey
                ? item.alternativeKey
                : item.key,
              keyCode: item.keyCode,
            }
          });

          return _.isEqual(sortedCurrentKeys, simpleShortcut) 
            || _.isEqual(sortedCurrentKeys, simpleShortcutAlternative);
        });

        //handle clipboard
        this.handleClipboard(e);
        this.clearCurrentKeys();
      }

      if(command) {
        //Execute command if not empty
        this.executeShortcutCommands(command);
      }
    },

    clearCurrentKeys() {
      if (this.currentKeys.length > 0) {
        this.currentKeys = [];
      }
    },

    executeShortcutCommands(commands) {
      commands.map(command => {
        switch(command) {
          case SHORTCUT_COMMANDS.NEXT_ORGANIZATION:
            this.goToNextOrganizationMention();
            break;
          case SHORTCUT_COMMANDS.NEXT_PARAGRAPH:
            this.goToNextParagraph();
            break;
          case SHORTCUT_COMMANDS.PREVIOUS_PARAGRAPH:
            this.goToPreviousParagraph();
            break;
          case SHORTCUT_COMMANDS.PARAGRAPH_REDUCE_SENTENCE:
            if(this.currentParagraphIndex !== -1)
              this.paragraphReduceSentence();
            break;
          case SHORTCUT_COMMANDS.PARAGRAPH_ADD_SENTENCE:
            if(this.currentParagraphIndex !== -1)
              this.paragraphIncreaseSentence();
            break;
          case SHORTCUT_COMMANDS.TOP_LINE:
            this.setActiveTab(CODING_TABS.TOPLINE);
            break;
          case SHORTCUT_COMMANDS.SOURCES:
            if(this.activeOrganization !== '') {
              this.setActiveTab(CODING_TABS.SOURCES);
            }
            break;
          case SHORTCUT_COMMANDS.OUTLETS:
            this.setActiveTab(CODING_TABS.OUTLETS);
            break;
          case SHORTCUT_COMMANDS.EDITOR:
            this.setActiveTab(CODING_TABS.EDITOR);
            break;
          case SHORTCUT_COMMANDS.CATEGORY_TAGS_MENU:
            this.updateTagsMenuData(true, TAG_TYPE.CATEGORY, false);
            break;
          case SHORTCUT_COMMANDS.ORGANIZATION_TAGS_MENU:
            var tagKey = undefined;
            var menu = TAG_TYPE.ORGANIZATION;
            this.menuType = TAG_TYPE.ORGANIZATION;
            if(this.isUseOrgWithCategory && this.projectSetupGeneral.org_category_key) {
              tagKey = this.projectSetupGeneral.org_category_key;
              menu = TAG_TYPE.CATEGORY;
            }
            this.updateTagsMenuData(true, menu, false, '', tagKey);
            break;
          case SHORTCUT_COMMANDS.SEARCH:
            if(!this.isTagsMenuOpen) {
              this.updateTagsMenuData(true, TAG_TYPE.CATEGORY, false);
            }
            this.$refs.tagsMenu.$refs.tagSearchInput.focus();
            break;
          case SHORTCUT_COMMANDS.ADD_TAG:
            this.addMentionTagSentimentFromClipboard();
            break;
          case SHORTCUT_COMMANDS.CLEAR_CLIPBOARD:
            if(this.clipboardText.length > 0) {
              this.clearClipboard();
              //Back to categories
              this.updateTagsMenuData(true, this.currentTagsType, false);
            }
            break;
          case SHORTCUT_COMMANDS.OPTIONS:
            this.$refs.articleWrapper.toggleShortcutOptionsModal(true);
            break;
          case SHORTCUT_COMMANDS.PUBLISH_CLIP:
            this.onPublishAnalysisClick();
            break;
          case SHORTCUT_COMMANDS.NEXT_CLIP:
            this.saveAndProceed();
            break;
          case SHORTCUT_COMMANDS.PREVIOUS_CLIP:
            this.saveAndProceed(true);
            break;
          case SHORTCUT_COMMANDS.UNDO:
            this.undoAction();
            break;
          case SHORTCUT_COMMANDS.BACK_TO_CATEGORY_TAGS_MENU:
            if(this.isTagsMenuOpen && this.currentTagsType === TAG_TYPE.CATEGORY && this.categoryKey) {
              this.updateTagsMenuData(true, TAG_TYPE.CATEGORY, this.isTagsMenuOverflow, this.searchQuery);
            }
            else if (this.isTagsMenuOpen) {
              this.updateTagsMenuData(false)
            }
            break;
          case SHORTCUT_COMMANDS.BACK_TO_ARTICLE_LIST:
            this.onBackToArticlesClick();
            break;
        }
      });
    },
    handleClipboard (e) {
      const pressedKey = e.keyCode || e.which;
      const allowed = e.key.match(/[0-9./+-]/g);

      if(allowed && allowed.length === 1 && ['textarea', 'input'].indexOf(e.target.nodeName.toLowerCase()) === -1) {
        this.updateClipboard(e.key);
      }

      //Back space
      if(pressedKey === 8 && this.clipboardText.length > 0){
        let clipboard = this.clipboardText.substring(0, this.clipboardText.length - 1);
        this.clearClipboard();
        this.updateClipboard(clipboard);
      }

      const listeningClean = this.clipboardText.replace(/\//g, '');
      const listeningTag = listeningClean && this.getTag(listeningClean);

      if (pressedKey !== 13 && this.clipboardText) {
        //If selecting a sub category
        if (listeningTag) {
          if (this.isTagsMenuOpen && this.currentTagsType === TAG_TYPE.CATEGORY && listeningTag.category !== TAG_CATEGORY.ORGANIZATION) {
            
            let searchQuery = this.clipboardText
            let shortcutSearch = true
            let tagKey = parseInt(this.clipboardText)

            this.updateTagsMenuData(true, TAG_TYPE.CATEGORY, false, searchQuery, tagKey, shortcutSearch);
          }

          if (this.clipboardText.indexOf('.') > -1) {
            this.selectedTag = listeningTag;
          }

          if (pressedKey === 190) {
            this.updateClipboard(e.key);
          }
        } else if (!this.clipboardText.endsWith('.')) {
          // Tag was not found in brief mappings. Search it in regular categories and load into brief mappings.
          this.updateTagsMenuData(true, TAG_TYPE.CATEGORY, false, this.clipboardText, listeningClean, true);
        }
      }

      this.delayFunction(this.clearClipboard, 4000);
    },

    setArticleContent (analysisArticleData) {
      this.articleContent = (analysisArticleData.body || analysisArticleData.translated_content) && _.cloneDeep(this.showTranslation ? analysisArticleData.translated_content : analysisArticleData.body).filter(c => {
        return !c.content || (c.content && c.content.trim() !== '')
      });
      this.articleHeadline = _.cloneDeep(this.showTranslation ? analysisArticleData.translated_headline : analysisArticleData.headline);

      Object.values(this.mentions || {}).map(mention => {
        this.highlight(mention, false, true);
      });

      //highlight auto-detected tags
      analysisArticleData.highlights && analysisArticleData.highlights.map(tag => {
        this.highlight(tag, false, true);
      });

      this.delayFunction(() => {
        document.querySelectorAll('span.highlight').forEach(element => element.addEventListener('click', this.addHighlightedMention))
      }, 1000)
    },

    highlight (tagObj, caseSensitive, forceHighlight) {
      let articleContent = this.articleContent;

      let tags = tagObj?.name ? [tagObj.name] : [];
      if(tagObj?.alias && tagObj.alias.length > 0){
        tags = _.uniq(tags.concat(tagObj.alias));
      }

      tags.map(tag => {
        // forceHighlight should be set to true when we are calling highlight after resetting articleHeadline and
        // articleContent to the state body value which does not have highlights set
        const alreadyHighlighted = !forceHighlight && this.checkHighlightExists(tagObj.category, tag);

        if(tag && !alreadyHighlighted) {
          const regexp = new RegExp(`\\b(?![^<]*>)(${tag.trim()})\\b`, caseSensitive ? "gm" : "gmi");

          let classes = ['highlight', editor.stringToClass(tagObj.category), editor.stringToClass(tag)];
          if (tagObj.selectability === 'onlyOne' && tagObj.parent) {
            classes.push('tag-parent-' + tagObj.parent);
          }

          const spanElement = document.createElement('span');
          if(tagObj.color) {
            spanElement.style.background = tagObj.color;
          }

          spanElement.classList.add(...classes);
          spanElement.setAttribute('data-key', tagObj.key);
          spanElement.innerHTML = '$1';

          let articleHeadline = this.articleHeadline;
          if (articleHeadline && articleHeadline.match(regexp) && articleHeadline.match(regexp).length > 0) {
            this.articleHeadline = articleHeadline.replace(regexp, spanElement.outerHTML);
          }

          articleContent.map(par => {
            if (par.content && par.content.match(regexp) && par.content.match(regexp).length > 0) {
              par.content = par.content.replace(regexp, spanElement.outerHTML);
            }
          });
        }
      });

      this.articleContent = articleContent;

      if (tagObj.tags) {
        tagObj.tags.map(childTag => this.highlight(childTag, caseSensitive, forceHighlight));
      }
    },

    removeHighlight (tagObj) {
      if (!tagObj) return;
      let articleContent = this.articleContent;

      let tags = [tagObj.name];
      if(tagObj.alias && tagObj.alias.length > 0) {
        tags = _.uniq(tags.concat(tagObj.alias));
      }

      tags.map(tag => {
        const existingHighlight = this.checkHighlightExists(tagObj.category, tag);
        const isHighlightInDetected = this.checkHighlightInDetected(tagObj);
        if (!existingHighlight || isHighlightInDetected) {
          return;
        }

        //Check if the tag is not added in another mention
        const mentionUsed = this.isTagInMentions(tagObj);

        if (!mentionUsed) {
          const regexpSpecialChar = new RegExp('(\\/|\\.|\\(|\\))', 'gmi')
          const regexpRemoveHighlight = new RegExp(existingHighlight.outerHTML.replace(regexpSpecialChar, '\\$1'), "gmi");

          let articleHeadline = this.articleHeadline;
          if (articleHeadline && articleHeadline.match(regexpRemoveHighlight) && articleHeadline.match(regexpRemoveHighlight).length > 0) {
            this.articleHeadline = articleHeadline.replace(regexpRemoveHighlight, existingHighlight.innerText);
          }

          articleContent.map(par => {
            if(par.content.match(regexpRemoveHighlight) && par.content.match(regexpRemoveHighlight).length > 0){
              par.content = par.content.replace(regexpRemoveHighlight, existingHighlight.innerText);
            }
          });
        }
      });

      this.articleContent = articleContent;

      if (tagObj.tags) {
        tagObj.tags.map(childTag => this.removeHighlight(childTag));
      }
    },

    checkHighlightExists (tagCategory, tagName) {
      if(this.$refs.articleWrapper && this.$refs.articleWrapper.$refs.article) {
        const articleContentElement = this.$refs.articleWrapper.$refs.article.$el;

        let existingHighlight = articleContentElement.getElementsByClassName(editor.stringToClass(tagCategory) + ' ' + editor.stringToClass(tagName));
        if (existingHighlight && existingHighlight[0]) {
          return existingHighlight[0];
        }
      }

      return false;
    },

    checkHighlightInDetected (tag) {
      return this.analysisArticle.data.data.highlights.find(highlightTag => highlightTag.key === tag.key);
    },

    addHighlightedMention(e) {
      e.preventDefault();
      e.stopPropagation();

      const tag = this.getTag(e.target.dataset.key);
      this.addMentionTag(tag);
    },

    isTagInMentions (tag) {
      let isMentioned = false;

      Object.values(this.mentions || {}).find(mention => {
        if(mention.category === tag.category && mention.name === tag.name) {
          isMentioned = true;
        }

        if(mention.tags) {
          mention.tags.map(mentionTag => {
            if(mentionTag.category === tag.category && mentionTag.name === tag.name) {
              isMentioned = true;
            }
          })
        }
      });

      return isMentioned;
    },

    goToNextOrganizationMention () {
      const mentionKeys = Object.keys(this.mentions || {}).filter(key => key !== 'articleLevel');

      if(this.activeOrganization === '') {
        this.setActiveOrganization(this.getDefaultActiveOrganization(this.mentions));
      }
      else if(mentionKeys.length > 1) {
        const activeOrganizationIndex = mentionKeys.findIndex(mention => mention === this.activeOrganization);

        const newIndex = activeOrganizationIndex + 1;
        const newActiveOrganization = mentionKeys.length -1 >= newIndex ? mentionKeys[newIndex] : mentionKeys[0];
        this.setActiveOrganization(newActiveOrganization);
      }
    },
    goToNextParagraph () {
      let currentParagraphIndex = this.currentParagraphIndex;
      const articleContent = (_.get(this.analysisArticle, 'data.data.body') || []).filter(c => {
        return !c.content || (c.content && c.content.trim() !== '')
      });
      if(currentParagraphIndex === -1) {
        this.currentParagraphIndex = 0;
      }
      else if(articleContent.length > 1) {
        const nextIndex = currentParagraphIndex + 1;
        this.currentParagraphIndex = articleContent.length -1 >= nextIndex ? nextIndex : 0;
      }

      this.noOfSelectedSentences = -1;
      this.scrollToParagraph();
    },
    goToPreviousParagraph () {
      let currentParagraphIndex = this.currentParagraphIndex;
      const articleContent = (_.get(this.analysisArticle, 'data.data.body') || []).filter(c => {
        return !c.content || (c.content && c.content.trim() !== '')
      });
      if(currentParagraphIndex === -1) {
        this.currentParagraphIndex = articleContent.length - 1;
      }
      else if(articleContent.length > 1) {
        const previousIndex = currentParagraphIndex - 1;
        this.currentParagraphIndex = previousIndex >= 0 ? previousIndex : articleContent.length - 1;
      }

      this.noOfSelectedSentences = -1;
      this.scrollToParagraph();
    },
    scrollToParagraph () {
      const currentParagraphElement = document.getElementById(`editor-content-${this.currentParagraphIndex}`);
      const bounding = currentParagraphElement.getBoundingClientRect();
      if (bounding.top < 0 || bounding.bottom > (window.innerHeight || document.documentElement.clientHeight)) {
        currentParagraphElement.scrollIntoView();
      }
    },
    paragraphReduceSentence () {
      let currentParagraphIndex = this.currentParagraphIndex;
      const articleBody = (_.get(this.analysisArticle, 'data.data.body') || []);
      const sentences = articleBody[currentParagraphIndex] ? articleBody[currentParagraphIndex].content.split('. ') : [];

      if(sentences.length <= 1 || this.noOfSelectedSentences === 1) {
        return;
      }

      if(this.noOfSelectedSentences === -1) {
        //get paragraph and set this.noOfSelectedSentences to the sentence before last
        if(currentParagraphIndex !== -1 && articleBody[currentParagraphIndex]) {
          this.noOfSelectedSentences = sentences.length - 1;
        }
      }
      else {
        //reduce this.noOfSelectedSentences by 1
        this.noOfSelectedSentences--;
      }
    },
    paragraphIncreaseSentence () {
      let currentParagraphIndex = this.currentParagraphIndex;
      const articleBody = (_.get(this.analysisArticle, 'data.data.body') || []);
      const sentences = articleBody[currentParagraphIndex].content.split('. ');

      if(this.noOfSelectedSentences !== -1 && sentences.length > 1 && sentences.length > this.noOfSelectedSentences) {
        //increase this.noOfSelectedSentences by 1
        this.noOfSelectedSentences++;
      }
    },
    undoAction () {
      let actionLength = this.coderActions.length;
      if (actionLength > 0) {
        const action = this.coderActions[actionLength-1];

        this.undoRelationshipLineAction({
          projectId: this.projectId,
          articleId: this.articleId,
          relationshipLineId: action.relationshipLineId,
          organizationKey: action.organization,
          data: action.mentions
        }).then((response) => {
          this.updateMentions(response);

          let activeOrganization = this.activeOrganization;
          if(action.organization !== TAG_CATEGORY.ARTICLE_LEVEL && action.organization !== TAG_CATEGORY.ARTICLELEVEL &&
            this.mentions[action.organization]) {
            activeOrganization = action.organization;
          }
          else if (activeOrganization === '' || !this.mentions[activeOrganization]) {
            activeOrganization = this.getDefaultActiveOrganization(this.mentions);
          }
          this.setActiveOrganization(activeOrganization);

          this.setArticleContent(this.analysisArticle.data.data);
          this.coderActions.pop();
        });
      }
    },
    setActiveTab (tabName) {
      if(this.activeTab !== tabName) {
        this.activeTab = tabName;
      }
    },
    updateClipboard (key){
      this.clipboardText += key;
    },
    clearClipboard (){
      this.clipboardText = '';
      this.selectedTag = undefined
    },
    delayFunction (callback, delayMs) {
      if(this.globalTimeout != null)
        clearTimeout(this.globalTimeout);

      this.globalTimeout = setTimeout(callback, delayMs);
    },
    addMentionTagSentimentFromClipboard () {
      if(!_.isEmpty(this.selectedTag) && this.clipboardText !== '') {
        const clipboardText = this.clipboardText;
        let tag = this.selectedTag;

        //if tag is already in mentions load tag
        const organization = this.getTagOrganization(tag);
        let mentionTagAdded = false;
        if (tag.category === TAG_CATEGORY.ORGANIZATION) {
          mentionTagAdded = this.mentions[organization];
        }
        else {
          mentionTagAdded = (_.get(this.mentions, `[${organization}].tags`) || []).find(t => t.key === this.selectedTag.key);
        }

        //Produce a function to be run once the organization / tag is added so that we can add also the score if requested
        const showSentiment = this.selectedTag.show_sentiment;
        let actionAfter = () => {
          if(showSentiment) {
            //check if there are any + or - after the number
            this.updateSentimentPointsFromClipboard(clipboardText, !mentionTagAdded);
          }

          this.selectedTag = undefined;
        };

        //if tag is new for mentions add it otherwise set selected tag to be used when updating sentiment
        if (!mentionTagAdded) {
          this.addMentionTag(this.selectedTag, false, actionAfter);
        }
        else {
          this.selectedTag = mentionTagAdded;

          if (typeof actionAfter === 'function') {
            actionAfter();
          }
        }

        this.clearClipboard();
      }
    },
    updateSentimentPointsFromClipboard (clipboardText, newTag) {
      if(!this.showScoringSystem)
        return;

      let selectedTag = _.clone(this.selectedTag);

      const sentiment = clipboardText.replace(selectedTag.key, '').split('');
      if(sentiment.length > 0 && !newTag) {
        //log mentions before modification of sentiment
        this.recordAction(this.selectedTag);
      }

      let positiveScore = 0; let negativeScore = 0;
      sentiment.forEach(action => {
        if(action === '+') {
          positiveScore++;
        }
        else if(action === '-') {
          negativeScore++;
        }
      });

      selectedTag = this.updateMentionSentiment({
        tag: selectedTag,
        isPositive: true,
        scoreUpdated: { positiveScore, negativeScore }});

    },
    showRelationTagMenu({tag, clickPosition}) {
      this.updateTagsMenuData(false);
      this.relationTag = tag;
      this.relationTagsMenuPosition = clickPosition;
      this.showRelationMenu = true;
    },
    closeRelationTagMenu(e) {
      if(e) {
        e.preventDefault();
        e.stopPropagation();
      }

      this.showRelationMenu = false;
    },
    cloneOrganizationMentions ({cloneFrom, cloneTo}) {
      let cloneToTag, tagToReplaceId, tagToAddId;
      const cloneToNew = !this.mentions[cloneTo];
      if(cloneToNew) {
        cloneToTag = Object.values(this.tags.organizationData.data).find(tag => editor.stringToClass(tag.name) === cloneTo);
        if(this.isUseOrgWithCategory){
          tagToReplaceId = (this.mentions?.[cloneFrom]?.tags || []).find(t => t.tag_category.is_organization_category)?.id;
          tagToAddId = cloneToTag?.parent_tag_id;
        }
      }
      else {
        cloneToTag = this.mentions[cloneTo];
        this.recordAction(cloneToTag);
      }

      let articleLevelMentions;
      if(this.mentions[TAG_CATEGORY.ARTICLELEVEL]) {
        articleLevelMentions = _.clone(this.mentions[TAG_CATEGORY.ARTICLELEVEL]);
      }

      this.cloneRelationshipLine({
        projectId: this.projectId,
        articleId: this.articleId,
        relationshipLineId: this.mentions[cloneFrom].relationship_line_id,
        cloneTo: cloneToTag.id,
        tagToReplaceId, 
        tagToAddId
      }).then((response) => {
        this.updateMentions(response);
        if(cloneToNew) {
          this.recordAction(response[cloneTo], true, articleLevelMentions);
        }

        this.activeOrganization = editor.stringToClass(cloneToTag.name);
      });
    },
    onPublishAnalysisClick () {
      if (!this.analysisArticle.data.data.is_published) {
        this.publishClipMentions(true);
      }
      else {
        this.toggleUnpublishConfirmationModal(true);
      }
    },
    toggleUnpublishConfirmationModal (open) {
      this.showUnpublishConfirmationModal = open;
    },
    isMissingRequiredCategory() {
      if (!this.missingCategories.length) return false;

      let totalContent = 0;
      if (this.missingCategoriesByType.content && this.missingCategoriesByType.content.length) {
        this.missingCategoriesByType.content
          .forEach(item => {
            totalContent += item.missingCategories.length
          });
      }

      this.$notify({
        group: "store_notifications",
        type: "error",
        text: this.$tc(
          "tag.form.errors.category_missing",
          this.missingCategories.length,
          { amount: this.missingCategoriesByType.article.length + totalContent }
        )
      });
      
      return true;
    },
    publishClipMentions (publish) {
      const self = this;
      if (publish && this.isMissingRequiredCategory()) return;

      if (!this.analysisArticlePublish.isBusy) {
        this.publishAnalysisArticle({
          projectId: this.projectId,
          articleId: this.articleId,
          publish: { is_published: publish }
        })
          .then((data) => {
            this.refreshAnalysisArticle({ data: { mentions: data.mentions, is_published: data.is_published } });
            if (data.mentions.articleLevel) {
              this.mentions.articleLevel = data.mentions.articleLevel
            }
            // this.mentions.articleLevel = data.mentions.articleLevel ? data.mentions.articleLevel : [];
            this.updateMentions((data && data.mentions) || this.mentions);
            this.key++;
          });
      }
      if (this.showUnpublishConfirmationModal) {
        this.toggleUnpublishConfirmationModal(false);
      }
    },
    saveAndProceed (previous) {
      const analysisArticle = this.analysisArticle.data;

      const newClip = previous ? analysisArticle.pagination.previousArticle.id : analysisArticle.pagination.nextArticle.id;
      if(!newClip || _.isNull(newClip)) {
        return;
      }

      //Redirect to next clip/article
      this.currentClip = newClip;
      this.redirectToClip(this.currentClip);
    },
    redirectToClip (clipId) {
      this.setArticleIsPublished(null);
      let url = `/analysis/editor/${this.projectId}/${clipId}`;

      if (window.location.search) url+= window.location.search;
      return this.$router.push(url);
    },
    saveArticleOutlet(outlet) {
      if(!this.analysisArticle.isBusy) {
        this.updateAnalysisArticle({
          projectId: this.projectId,
          articleId: this.articleId,
          analysisArticle: { outlet: outlet },
          forceCall: true
        });
      }
    },
    saveArticleTopLine ( { topLineModel, isAuthors = false, requestSave = true }) {
      this.updateArticleTopLine({
        projectId: this.projectId,
        articleId: this.articleId,
        topLine: topLineModel,
        requestSave
      });

      this.isToplineUpdated = true;
      this.resetContent = topLineModel.headline && this.articleTopLine.data && (!this.articleTopLine.data.headline || topLineModel.headline !== this.articleTopLine.data.headline.value);
    },
    updateArticleTopLineMetrics ( { topLineModel, isAuthors = false }) {
      this.refreshArticleTopLineMetrics({
        projectId: this.projectId,
        articleId: this.articleId,
        topLine: topLineModel
      });
    },
    saveTranslations () {
      if (!this.articleTopLine.isBusy) {
        this.updateTranslations({ projectId: this.projectId, articleId: this.articleId });
      }
    },
    appendSourceMentionQuote (quote) {
      const activeQuoteIndex = this.activeQuoteIndex;
      const newValue = this.$refs.sidebar.$refs.sources.$refs['sourceQuote'+activeQuoteIndex][0].value + quote;
      this.$refs.sidebar.$refs.sources.onSourceMentionQuoteChange(newValue, activeQuoteIndex);
    },
    setActiveSource (source) {
      this.activeSource = source;
    },
    setActiveQuoteIndex (quoteIndex = -1) {
      this.activeQuoteIndex = quoteIndex;
    },
    removeTagFromMentions (tag) {
      Object.values(this.mentions).find(mention => {
        if(mention.tags) {
          mention.tags.map(mentionTag => {
            if(mentionTag.key === tag.key) {
              this.deleteMentionTag(tag, editor.stringToClass(mention.name));
            }
          })
        }
      });
    },
    updateTagInMentions (tag) {
      Object.values(this.mentions).find(mention => {
        if(mention.tags) {
          mention.tags.map(mentionTag => {
            if(mentionTag.key === tag.key) {
              mentionTag = tag;
            }
          })
        }
      });

      this.updateMentions();
    },
    onFlagAsIrrelevantClick (e) {
      if(e) {
        e.preventDefault();
        e.stopPropagation();
      }

      this.toggleIrrelevantConfirmationModal(true);
    },
    flagAsIrrelevantConfirm () {
      const { projectId, analysisArticle, isAnalysisIrrelevant } = this;
      this.flagAsIrrelevant({ projectId, articleId: analysisArticle.data.data.id, irrelevant: !isAnalysisIrrelevant });

      if (this.showIrrelevantConfirmationModal) {
        this.toggleIrrelevantConfirmationModal(false);
      }
    },
    toggleIrrelevantConfirmationModal (open) {
      this.showIrrelevantConfirmationModal = open;
    },
    toggleShowTranslation (show, e){
      if(e) {
        e.preventDefault();
        e.stopPropagation();
      }
      this.showTranslation = show;
      this.setArticleContent(this.analysisArticle.data.data);
    },
    closeModal () {
      this.aiCodingData = undefined;
    }
  },
  data () {
    return {
      key: 1,
      allTagsCategories: [],
      isTagsMenuPinned: false,
      isTagsMenuOpen: false,
      isTagsMenuOverflow: undefined,
      currentTagsType: '',
      displayTags: {},
      categoryList: {},
      categoryKey: undefined,
      searchQuery: '',
      relationSearch: '',
      mentions: {},
      contentMentions: [],
      articleMentions: [],
      activeOrganization: '',
      coderActions: [],
      currentKeys: [],
      activeTab: CODING_TABS.EDITOR,
      activeSource: undefined,
      activeQuoteIndex: -1,
      currentParagraphIndex: -1,
      noOfSelectedSentences: -1,
      clipboardText: '',
      globalTimeout: null,
      selectedTag: undefined,
      showRelationMenu: false,
      relationTag: undefined,
      relationTagsMenuPosition: undefined,
      isToplineUpdated: false,
      resetContent:false,
      currentClip: undefined,
      showBackConfirmationModal: false,
      showUnpublishConfirmationModal: false,
      showIrrelevantConfirmationModal: false,
      forceQuit: false,
      articleContent: [],
      articleHeadline: '',
      showTranslation: false,
      pendingMentionTag: {},
      aiCodingData: undefined,
      menuType: '',
      parentTagId: null,
      organizationIsBusy: false
    }
  },

  filters: {
    formatDate: function (value) {
      if (!value) return '';
      return moment.utc(value).format('D MMM YYYY [at] HH:mm');
    }
  }
}
</script>

<style lang="scss">
@import "~@/styles/components/coding-ui/layout.scss";
</style>